import { QueryClient, QueryCacheNotifyEvent } from '@tanstack/react-query';
import type { Query } from '@tanstack/react-query';
import type { CubeQuery, CubeQueryRecord } from '@/dev/types/cubeQueryTypes';
import { deriveContextFromQueryKey, deriveContextFromQuery } from '@/dev/services/componentNameExtractor';
import { QueryStore } from '@/dev/services/QueryStore';
import { TabStateManager } from '@/dev/services/TabStateManager';
import { isCubeQuery } from '@/dev/utils/cubeQueryGuards';
import { getResponseRowCount } from '@/dev/utils/cubeQueryRunnerUtils';

function findCubeQueryInKey(queryKey: ReadonlyArray<unknown>): CubeQuery | undefined {
  for (const part of queryKey) {
    if (isCubeQuery(part)) {
      return part;
    }
  }
  return undefined;
}

const getCurrentPagePath = (fallbackPath: string): string => {
  if (typeof window === 'undefined') return fallbackPath;
  return window.location.pathname;
};

const getErrorStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;
  if ('status' in error && typeof error.status === 'number') return error.status;
  if ('response' in error) return getErrorStatus(error.response);
  return undefined;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Cube query failed';
};

interface CaptureOptions {
  dataUpdatedAt?: number;
  duration?: number;
  error?: string;
  rowCount?: number;
  status: number;
}

export class CacheSubscriber {
  private queryClient: QueryClient | null = null;

  private unsubscribe: (() => void) | null = null;

  private capturedQueryHashes = new Set<string>();

  private fetchStartedAt = new Map<string, number>();

  private currentPagePath = '/';

  constructor(
    private queryStore: QueryStore,
    private tabStateManager: TabStateManager,
    private getCubejsApiUrl: () => string,
    private isRecentNetworkQuery: (hash: string) => boolean,
    private generateRequestId: () => string,
  ) {}

  setQueryClient(client: QueryClient): void {
    this.queryClient = client;
  }

  startListening(): void {
    if (!this.queryClient || this.unsubscribe) return;

    const cache = this.queryClient.getQueryCache();
    this.unsubscribe = cache.subscribe(this.handleCacheEvent);
  }

  stopListening(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.fetchStartedAt.clear();
  }

  updatePagePath(pagePath: string): void {
    this.currentPagePath = pagePath;
    this.capturedQueryHashes.clear();
    this.fetchStartedAt.clear();
  }

  clearCapturedHashes(): void {
    this.capturedQueryHashes.clear();
    this.fetchStartedAt.clear();
  }

  private addQueryRecord(query: Query, cubeQuery: CubeQuery, pagePath: string, options: CaptureOptions): void {
    if (this.queryStore.isFull()) return;

    const componentName =
      deriveContextFromQueryKey(Array.from(query.queryKey)) || deriveContextFromQuery(cubeQuery) || 'CachedQuery';
    const record: CubeQueryRecord = {
      id: this.generateRequestId(),
      timestamp: Date.now(),
      componentName,
      pagePath,
      tabName: this.tabStateManager.getActiveTab() || undefined,
      query: cubeQuery,
      queryKey: query.queryKey.filter((key): key is string => typeof key === 'string'),
      url: this.getCubejsApiUrl(),
      method: 'CUBE_LOAD',
      ...(options.duration === undefined ? {} : { duration: options.duration }),
      ...(options.rowCount === undefined ? {} : { rowCount: options.rowCount }),
      ...(options.error === undefined ? {} : { error: options.error }),
      ...(options.dataUpdatedAt === undefined ? {} : { dataUpdatedAt: options.dataUpdatedAt }),
      status: options.status,
      dataSource: 'cache',
      cached: true,
    };

    this.queryStore.addQuery(record);
  }

  private handleCacheEvent = (event: QueryCacheNotifyEvent): void => {
    if (!this.queryStore.getRecording()) return;

    if (event.type !== 'updated' && event.type !== 'observerResultsUpdated' && event.type !== 'observerAdded') return;

    const { query } = event;

    if (!Array.isArray(query.queryKey)) return;

    const cubeQuery = findCubeQueryInKey(query.queryKey);
    if (!cubeQuery) return;

    if (event.type === 'updated' && event.action.type === 'fetch') {
      this.fetchStartedAt.set(query.queryHash, performance.now());
      return;
    }

    const startedAt = this.fetchStartedAt.get(query.queryHash);
    const isSuccessfulFetch = event.type === 'updated' && event.action.type === 'success';

    if (event.type === 'updated' && event.action.type === 'error') {
      if (startedAt === undefined) return;

      const duration = Math.max(0, Math.round(performance.now() - startedAt));
      this.fetchStartedAt.delete(query.queryHash);
      const queryHash = JSON.stringify(cubeQuery);
      if (this.isRecentNetworkQuery(queryHash)) return;

      this.addQueryRecord(query, cubeQuery, getCurrentPagePath(this.currentPagePath), {
        duration,
        error: getErrorMessage(event.action.error),
        status: getErrorStatus(event.action.error) ?? 0,
      });
      return;
    }

    if (startedAt !== undefined && !isSuccessfulFetch) return;
    if (startedAt === undefined && query.getObserversCount() === 0) return;

    const duration = startedAt === undefined ? undefined : Math.max(0, Math.round(performance.now() - startedAt));
    if (isSuccessfulFetch) this.fetchStartedAt.delete(query.queryHash);

    const queryHash = JSON.stringify(cubeQuery);
    const pagePath = getCurrentPagePath(this.currentPagePath);
    const capturedQueryKey = `${pagePath}:${queryHash}`;

    const isMeasuredFetch = duration !== undefined;
    if (!isMeasuredFetch && this.capturedQueryHashes.has(capturedQueryKey)) return;
    if (this.isRecentNetworkQuery(queryHash)) return;

    if (!query.state.dataUpdatedAt) return;

    if (!isMeasuredFetch) this.capturedQueryHashes.add(capturedQueryKey);

    const rowCount = getResponseRowCount(query.state.data);
    this.addQueryRecord(query, cubeQuery, pagePath, {
      dataUpdatedAt: query.state.dataUpdatedAt,
      duration,
      rowCount,
      status: 200,
    });
  };
}
